/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import debounce from "../../Utils/debounce";

type OptionsQuery<T> = (suchbegriff: string) => Promise<T[]>;

const DEBOUNCE_MILLIS = 300;
const LOADING_MILLIS = 1000;

type OptionsResult<T> =
    | { loading: true }
    | { loading: false, value: T[] };

export default function useOptionsQuery<T>(query: OptionsQuery<T>) {
    const [options, setOptions] = React.useState<OptionsResult<T>>({ loading: false, value: [] });
    const mounted = React.useRef(false);

    async function execute(suchbegriff: string) {
        if (mounted.current) {
            setOptions(prev => {
                // Den Loading-State nicht anzeigen, wenn bereits Optionen in der Liste vorhanden
                // sind
                if (!prev.loading && prev.value.length > 0) {
                    return prev;
                }

                return { loading: true }
            });
        }

        // Wenn nach einer Sekunde, die Query immer noch am Laufen ist, dann doch den Loading-State
        // anzeigen
        const timeout = window.setTimeout(
            setOptions.bind(null, { loading: true }), LOADING_MILLIS);

        const options = await query(suchbegriff);
        if (mounted.current) {
            window.clearTimeout(timeout);
            setOptions({ loading: false, value: options });
        }
    }

    const executeDebounced = debounce(execute, DEBOUNCE_MILLIS);

    React.useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            executeDebounced.cancel();
        };
    }, []);

    return { options, execute, executeDebounced };
}